import { Badge } from "@package/subframe/components/Badge";
import { Table } from "@package/subframe/components/Table";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";

import { Pagination } from "@/components/ui/pagination";
import { Spinner } from "@/components/ui/spinner";
import { TableScrollContainer } from "@/components/ui/table-scroll-container";
import { LoginAttemptResponseDtoOutcome } from "@/generated/model";
import { useGetUser, useGetUserLoginAttempts } from "@/generated/users/users";

export const Route = createFileRoute(
  "/_authenticated/people/$userId/login-history",
)({
  component: LoginHistoryPage,
});

const DEFAULT_PAGE_SIZE = 20;

type OutcomeBadge = {
  variant: "success" | "warning" | "error" | "neutral" | "brand";
  label: string;
};

/**
 * Keyed off the generated enum so that adding an outcome server-side and
 * re-running `npm run generate:orval` makes this a type error until the new
 * value gets a badge — rather than a runtime crash on the first row that uses it.
 */
const OUTCOME_BADGE: Record<LoginAttemptResponseDtoOutcome, OutcomeBadge> = {
  [LoginAttemptResponseDtoOutcome.SUCCESS]: {
    variant: "success",
    label: "Success",
  },
  [LoginAttemptResponseDtoOutcome.FAILURE]: {
    variant: "warning",
    label: "Failed",
  },
  [LoginAttemptResponseDtoOutcome.LOCKED]: {
    variant: "error",
    label: "Locked",
  },
  [LoginAttemptResponseDtoOutcome.UNLOCKED]: {
    variant: "neutral",
    label: "Unlocked",
  },
  [LoginAttemptResponseDtoOutcome.PIN_RESET]: {
    variant: "brand",
    label: "PIN reset",
  },
  [LoginAttemptResponseDtoOutcome.PASSWORD_RESET]: {
    variant: "brand",
    label: "Password reset",
  },
  [LoginAttemptResponseDtoOutcome.ACCOUNT_DEACTIVATED]: {
    variant: "error",
    label: "Account deactivated",
  },
  [LoginAttemptResponseDtoOutcome.ACCOUNT_REACTIVATED]: {
    variant: "success",
    label: "Account reactivated",
  },
};

/**
 * Deployed clients can lag the API, so a value the bundle predates may still
 * arrive. Degrade to a readable neutral badge instead of crashing the page.
 */
function outcomeBadge(outcome: LoginAttemptResponseDtoOutcome): OutcomeBadge {
  return (
    OUTCOME_BADGE[outcome] ?? {
      variant: "neutral",
      label: String(outcome)
        .toLowerCase()
        .replace(/_/g, " ")
        .replace(/^./, (c) => c.toUpperCase()),
    }
  );
}

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString();
}

function LoginHistoryPage() {
  const { userId } = Route.useParams();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data: userResponse } = useGetUser(userId, {
    query: { refetchOnMount: "always" },
  });
  const user = userResponse?.data ?? null;
  const displayName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : (user?.email ?? "this user");

  // Always refetch on mount: a manager typically arrives here straight from
  // an account action, and the global query defaults would otherwise serve a
  // cached list that predates the reset row by up to five minutes.
  const { data, isLoading, isError } = useGetUserLoginAttempts(
    userId,
    { limit: pageSize, offset: (currentPage - 1) * pageSize },
    { query: { refetchOnMount: "always" } },
  );

  const attempts = data?.data.data ?? [];
  const total = data?.data.total ?? 0;

  return (
    <div className="flex w-full flex-col items-start gap-6 px-8 py-8">
      <div className="flex w-full flex-col items-start gap-1">
        <Link
          to="/people/$userId"
          params={{ userId }}
          search={{ tab: "login" }}
          className="text-caption font-caption text-subtext-color hover:text-default-font"
        >
          ← Back to profile
        </Link>
        <span className="text-heading-2 font-heading-2 text-neutral-900">
          Login history
        </span>
        <span className="text-body font-body text-subtext-color">
          PIN sign-ins and account changes for {displayName}.
        </span>
      </div>

      {isLoading ? (
        <div className="flex w-full items-center justify-center py-12">
          <Spinner />
        </div>
      ) : isError ? (
        <span className="text-body font-body text-error-700">
          Couldn&apos;t load login history. Please try again.
        </span>
      ) : total === 0 ? (
        <span className="text-body font-body text-subtext-color">
          Nothing recorded yet.
        </span>
      ) : (
        <>
          <TableScrollContainer>
            <Table
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell>Outcome</Table.HeaderCell>
                  <Table.HeaderCell>When</Table.HeaderCell>
                  <Table.HeaderCell>Device</Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {attempts.map((attempt) => {
                const badge = outcomeBadge(attempt.outcome);
                return (
                  <Table.Row key={attempt.id}>
                    <Table.Cell>
                      <Badge variant={badge.variant}>{badge.label}</Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-body font-body text-default-font">
                        {formatTimestamp(attempt.createdAt)}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-body font-body text-subtext-color">
                        {attempt.deviceId ?? "—"}
                      </span>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table>
          </TableScrollContainer>

          <Pagination
            page={currentPage}
            pageSize={pageSize}
            totalCount={total}
            itemNoun="attempts"
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
          />
        </>
      )}
    </div>
  );
}
